import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { updateProduct, getCategories, datasheetUrl, uploadDatasheet } from "@/lib/api";
import { num } from "@/lib/calc";
import { NUM_FIELDS, TXT_FIELDS, BOOL_FIELDS, numLabel } from "@/lib/fields";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileText, Download, Upload, CheckCircle2, AlertCircle } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

export default function EditProductDialog({ product, open, onClose, onSaved }) {
  const { t, tc } = useApp();
  const [data, setData] = useState(null);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    getCategories().then(setCats).catch(() => setCats([]));
  }, []);

  useEffect(() => {
    if (product) {
      setData({
        name: product.name, brand: product.brand, description: product.description || "",
        subcategory: product.subcategory, source_file: product.source_file || null,
        specs: { ...(product.specs || {}) },
      });
    }
  }, [product]);

  // Safety net for the known Radix bug where body `pointer-events:none` (and the dark
  // overlay) can stay stuck after closing on some browsers -> "black frozen screen".
  useEffect(() => {
    if (open) return;
    const clear = () => { if (document.body.style.pointerEvents === "none") document.body.style.pointerEvents = ""; };
    clear();
    const id = setTimeout(clear, 400);
    return () => clearTimeout(id);
  }, [open]);

  if (!data) return null;

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setSpec = (k, v) => setData((d) => ({ ...d, specs: { ...d.specs, [k]: v } }));

  const currentCat = cats.find((c) => c.subcategories.some((s) => s.id === data.subcategory))?.id || "";
  const subsForCat = cats.find((c) => c.id === currentCat)?.subcategories || [];
  const onCatChange = (catId) => {
    const c = cats.find((x) => x.id === catId);
    const firstSub = c?.subcategories?.[0]?.id;
    if (firstSub) setField("subcategory", firstSub);
  };

  const onDatasheet = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const updated = await uploadDatasheet(product.id, f);
      setField("source_file", updated.source_file);
      toast.success(t("savedOk"));
      onSaved && onSaved(updated);
    } catch {
      toast.error("Error");
    }
    e.target.value = "";
  };

  const save = async () => {
    if (!data.name || !data.brand) return toast.error("Name / Brand");
    try {
      const updated = await updateProduct(product.id, {
        name: data.name, brand: data.brand, subcategory: data.subcategory,
        description: data.description, specs: data.specs, source_file: data.source_file,
      });
      toast.success(t("savedOk"));
      onClose();                                           // close dialog before the list re-renders
      setTimeout(() => onSaved && onSaved(updated), 300);  // update list after close animation
    } catch (e) {
      toast.error("Error");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-none border-border p-0 gap-0 max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="border-b border-border px-5 py-3">
          <DialogTitle className="font-display text-lg tracking-tight">{t("edit")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 overflow-y-auto p-5">
          {(() => {
            const req = [
              { k: "price_per_kg", label: t("pricePerKg") },
              { k: "consumption_kg_per_m2", label: t("consumption") },
            ];
            const missing = req.filter((r) => !(num(data.specs?.[r.k]) > 0));
            return missing.length === 0 ? (
              <div data-testid="complete-status" className="flex items-center gap-2 border border-success/50 bg-success/10 px-3 py-2 text-sm">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
                <span className="font-semibold text-foreground">{t("completeOk")}</span>
              </div>
            ) : (
              <div data-testid="complete-status" className="border border-primary/50 bg-primary/10 px-3 py-2 text-sm">
                <div className="flex items-center gap-2 font-semibold text-foreground">
                  <AlertCircle className="h-4 w-4 shrink-0 text-primary" /> {t("completeReqTitle")}
                </div>
                <ul className="mt-1 list-disc pl-7 text-muted-foreground">
                  {missing.map((m) => (
                    <li key={m.k} data-testid={`missing-${m.k}`}>{m.label}</li>
                  ))}
                </ul>
              </div>
            );
          })()}
          <div className="grid grid-cols-2 gap-3">
            <Txt label={t("brand")} value={data.brand} onChange={(v) => setField("brand", v)} testid="edit-brand" />
            <Txt label={t("product")} value={data.name} onChange={(v) => setField("name", v)} testid="edit-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1.5">
              <span className="label-caps text-muted-foreground">{t("category")}</span>
              <Select value={currentCat} onValueChange={onCatChange}>
                <SelectTrigger data-testid="edit-category" className="rounded-none border-input bg-surface font-mono text-sm">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent className="rounded-none max-h-72">
                  {cats.map((c) => (
                    <SelectItem key={c.id} value={c.id} className="font-mono text-sm">{tc(c.id)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="label-caps text-muted-foreground">{t("subcategory")}</span>
              <Select value={data.subcategory} onValueChange={(v) => setField("subcategory", v)}>
                <SelectTrigger data-testid="edit-subcategory" className="rounded-none border-input bg-surface font-mono text-sm">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent className="rounded-none max-h-72">
                  {subsForCat.map((s) => (
                    <SelectItem key={s.id} value={s.id} className="font-mono text-sm">{tc(s.id)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {TXT_FIELDS.map(([k]) => (
              <Txt key={k} label={t(k)} value={data.specs?.[k] || ""} onChange={(v) => setSpec(k, v)} />
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {NUM_FIELDS.map(([k, lk]) => (
              <Num key={k} label={numLabel(t, k, lk)} field={k} value={data.specs?.[k]} onChange={(v) => setSpec(k, v)} />
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {BOOL_FIELDS.map((k) => {
              const active = data.specs?.[k] === true || data.specs?.[k] === "true";
              return (
                <button key={k} onClick={() => setSpec(k, !active)}
                  className={`border px-2.5 py-1.5 text-xs font-medium ${active ? "border-primary bg-primary/15 text-foreground" : "border-border text-muted-foreground"}`}>
                  {t(k)}
                </button>
              );
            })}
          </div>
          <div className="border border-border bg-surface p-3" data-testid="datasheet-section">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <span className="label-caps text-muted-foreground">{t("techData")}</span>
              <div className="ml-auto flex items-center gap-2">
                {data.source_file ? (
                  <>
                    <a data-testid="datasheet-view" href={datasheetUrl(product.id)} target="_blank" rel="noreferrer"
                      className="flex items-center gap-1 border border-border px-2.5 py-1.5 text-xs font-semibold hover:bg-background">
                      <Download className="h-3.5 w-3.5" /> {t("dsView")}
                    </a>
                    <label data-testid="datasheet-replace" className="flex cursor-pointer items-center gap-1 border border-border px-2.5 py-1.5 text-xs font-semibold hover:bg-background">
                      <Upload className="h-3.5 w-3.5" /> {t("dsReplace")}
                      <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={onDatasheet} />
                    </label>
                  </>
                ) : (
                  <label data-testid="datasheet-attach" className="flex cursor-pointer items-center gap-1 border border-primary/60 bg-primary/10 px-2.5 py-1.5 text-xs font-semibold hover:bg-primary/20">
                    <Upload className="h-3.5 w-3.5 text-primary" /> {t("dsAttach")}
                    <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={onDatasheet} />
                  </label>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" className="rounded-none" onClick={onClose}>{t("cancel")}</Button>
            <Button data-testid="edit-save-btn" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90" onClick={save}>{t("save")}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Txt({ label, value, onChange, testid }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="label-caps text-muted-foreground">{label}</span>
      <input data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="border border-input bg-surface px-2.5 py-2 font-mono text-sm outline-none focus:ring-1 focus:ring-primary" />
    </label>
  );
}

function Num({ label, field, value, onChange }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="label-caps text-muted-foreground truncate">{label}</span>
      <input type="number" data-testid={`edit-${field}`} value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : parseFloat(e.target.value))}
        className="border border-input bg-surface px-2.5 py-2 font-mono text-sm outline-none focus:ring-1 focus:ring-primary" />
    </label>
  );
}
